import { useMemo, useState } from 'react';
import type { FC } from 'react';
import styled, { css } from 'styled-components';
import type { PhotoProps } from './types';
import { ApiService, baseMediaEndpoint } from 'shared/helpers/api';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import { filterBackgroundColor } from 'shared/helpers/website';
import { generateAccessKey, sanitizeRichText } from 'shared/helpers/utils';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { HoverableDropzone } from '../HoverableDropzone';
import useSales from 'shared/hooks/useSales';
import { Button, Download, Wrapper } from '../Common';
import { FlexBox } from 'shared/components/Common';

import type { SalesValues } from 'shared/types/Sales';

const CardsInner = styled.div`
  padding: 80px 0 88px;
  display: flex;
  justify-content: space-between;

  ${({ theme: { $width } }) =>
    $width <= 1400 &&
    `
    flex-wrap: wrap;
    gap: 110px 3%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 70px 0 78px;
    flex-direction: column;
    gap: 70px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    flex-direction: column;
  `}
`;

const CardItem = styled.div<{ editable?: boolean }>`
  position: relative;
  width: 23%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  word-wrap: break-word;
  word-break: break-word;

  &:nth-child(1) {
    z-index: 5;
  }

  &:nth-child(2) {
    z-index: 4;
  }

  &:nth-child(3) {
    z-index: 3;
  }

  &:nth-child(4) {
    z-index: 2;
  }

  ${({ theme: { $width } }) =>
    $width <= 1400 &&
    `
    width: 47%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
    width: 100%;
  `}
`;

const ImageInner = styled.div`
  position: relative;
  margin-bottom: 36px;
  width: 100%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
        width: 100% !important;
    height: 470px !important;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100% !important;
     
    margin-bottom: 28px;
  `}
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const imageStyles = css`
  border: none;
  position: relative;
  max-width: 100%;
  width: 100%;
  height: 390px;
  overflow: hidden;
  object-fit: cover;
  max-width: 300px;

  ${({ theme: { $width } }) =>
    $width <= 1400 &&
    `
    max-width: 100% !important;
    width: 100% !important;
    height: 470px !important;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    max-width: 100% !important;
    width: 100% !important;
    height: 470px !important;
  `}
`;

const CardItemTitle = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  margin: 0px 0 12px;
  width: 100%;
  text-align: left;
  position: relative;
  min-height: inherit;
  line-height: 1.35em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    min-height: 50px;
    &:not(:focus):before {
      content: attr(data-placeholder);
      text-align: center;
      justify-content: center;
      cursor: text;
      color: #aaa;
      width: 100%;
      position: absolute;
      display: flex;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
  `}
`;

const CardItemContent = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin: 0 0 50px 0;
  width: 100%;
  text-align: left;
  position: relative;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    min-height: 40px;
    &:not(:focus):before {
      content: attr(data-placeholder);
      text-align: center;
      justify-content: center;
      cursor: text;
      color: #aaa;
      width: 100%;
      position: absolute;
      display: flex;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const BtnInner = styled.div`
  width: 100%;
  margin-top: auto;
  display: flex;
  justify-content: center;
`;

export const V15: FC<PhotoProps> = (props) => {
  const { benefits = [], textColor, meta, hidden, customBackgroundColor, order } = props;

  const [isLoading, setIsLoading] = useState([false, false, false, false]);

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { salesPageId, updateImageMeta, purchaseElementId, type, isDashboard } = useSales();

  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);
  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);

  // Accessibility improvements
  const skipLinkId = useMemo(() => `skip-to-photo-v15-${meta['accessorKey'] || 'photo'}`, [meta]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Four-card photo grid edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File, index: number) => {
    setIsLoading((prev) => prev.map((_, i) => (i === index - 1 ? true : false)));
    if (!meta['accessorKey'] || !editable) return;

    try {
      const formData = new FormData();
      formData.append('type', meta['accessorKey']);
      formData.append('file', file);
      const response = await ApiService.post(`/sales/sales-page/${salesPageId}/media/`, formData);
      updateImageMeta(meta['accessorKey'], response.data.file, `image_${index}`);
    } catch (error) {
      handleEditError(error);
    } finally {
      setIsLoading([false, false, false, false]);
    }
  };

  const images = useMemo(() => {
    const imageItems = [
      '/img/event_for_three_1.jpg',
      '/img/event_for_three_1.jpg',
      '/img/event_for_three_1.jpg',
      '/img/event_for_three_1.jpg'
    ];
    if (meta['image_1']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      imageItems[0] = meta['image_1'];
    }
    if (meta['image_2']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      imageItems[1] = meta['image_2'];
    }
    if (meta['image_3']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      imageItems[2] = meta['image_3'];
    }
    if (meta['image_4']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      imageItems[3] = meta['image_4'];
    }
    return imageItems;
  }, [meta]);

  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  return (
    <FlexBox
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: customBackgroundColor || '#fff'
      }}
      aria-label="Four-card photo grid with portrait images, text, and action buttons"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to four-card photo grid</a>
      </span>

      <Wrapper id={skipLinkId}>
        <CardsInner role="region" aria-label={`Four-card grid with ${benefits.length} items`}>
          {benefits.map(
            ({ title: benefitTitle, content: benefitContent, button: benefitButton }, index) => {
              const isTitleEmpty = checkTitleBenefits(index);
              const isContentEmpty = checkContentBenefits(index);
              const url = meta[`url_${index + 1}`];
              const titleAccessKey = generateAccessKey(`pft${index + 1}`, order);
              const contentAccessKey = generateAccessKey(`pfc${index + 1}`, order);
              const imageAccessKey = generateAccessKey(`pfi${index + 1}`, order);

              return (
                <CardItem
                  className="card-item__btn_choosen"
                  key={index}
                  editable={isDashboard}
                  role="article"
                  aria-label={`Grid card ${index + 1}: ${benefitTitle?.val || 'Product card'}`}>
                  <ImageInner
                    role="img"
                    aria-label={`Grid card ${index + 1} portrait image`}
                    {...(!isDashboard && {
                      accessKey: imageAccessKey,
                      title: `Press Alt+${imageAccessKey} to focus grid card ${index + 1} image`,
                      tabIndex: 0
                    })}>
                    <HoverableDropzone
                      isSalePage
                      css={imageStyles as never}
                      disabled={!editable}
                      allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
                      onUpload={(file) => handleUpload(file, index + 1)}
                      loading={isLoading[index]}
                      alreadyUploaded={Boolean(images[index])}
                      imageFile={images[index]}
                      enableLinks
                      size="md"
                      initialPosition={{
                        x: meta[`x_${index + 1}`] || 0,
                        y: meta[`y_${index + 1}`] || 0,
                        width: meta[`d_width_${index + 1}`],
                        height: meta[`d_height_${index + 1}`]
                      }}
                      meta={meta}
                      positionEnabled
                      handlePosition={(width: number, height: number, x: number, y: number) => {
                        updateImageMeta(meta['accessorKey'], x, `x_${index + 1}`);
                        updateImageMeta(meta['accessorKey'], y, `y_${index + 1}`);
                        updateImageMeta(meta['accessorKey'], width, `d_width_${index + 1}`);
                        updateImageMeta(meta['accessorKey'], height, `d_height_${index + 1}`);
                      }}
                      resizeSelector={editable}
                      handleResize={({ width, height }) => {
                        setResize({ width, height });
                        updateImageMeta(meta['accessorKey'], width, 'width');
                        updateImageMeta(meta['accessorKey'], height, 'height');
                      }}
                      imageAspectRatio={{
                        ratio: '3x4',
                        pixels: '360x480'
                      }}>
                      <CardImg
                        src={images[index]}
                        alt={`Grid card ${index + 1} portrait image showing relevant product or service content`}
                        width={resize.width || 500}
                        height={resize.height || 500}
                        role="img"
                        aria-describedby={benefitContent?.id}
                      />
                    </HoverableDropzone>
                  </ImageInner>

                  <FlexBox
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    role="region"
                    aria-label={`Grid card ${index + 1} text content`}>
                    <CardItemTitle
                      id={benefitTitle?.id}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(benefitTitle?.val, editable)
                      }}
                      style={{
                        color: filterBackgroundColor(customBackgroundColor || '#fff', textColor)
                      }}
                      isEmpty={isTitleEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'title',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label':
                              isTitleEmpty || !benefitTitle?.val?.trim()
                                ? `Grid card ${index + 1} title`
                                : undefined,
                            accessKey: titleAccessKey,
                            title: `Press Alt+${titleAccessKey} to focus grid card ${index + 1} title`,
                            tabIndex: 0
                          })}
                    />

                    <CardItemContent
                      id={benefitContent?.id}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(benefitContent?.val, editable)
                      }}
                      style={{
                        color: filterBackgroundColor(customBackgroundColor || '#fff', textColor)
                      }}
                      isEmpty={isContentEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'content',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            role: 'textbox',
                            'aria-multiline': 'true',
                            'aria-label':
                              isContentEmpty || !benefitContent?.val?.trim()
                                ? `Grid card ${index + 1} description`
                                : undefined,
                            'aria-describedby': benefitTitle?.id,
                            accessKey: contentAccessKey,
                            title: `Press Alt+${contentAccessKey} to focus grid card ${index + 1} description`,
                            tabIndex: 0
                          })}
                    />
                  </FlexBox>

                  <BtnInner>
                    {url || type === 'delivery' ? (
                      <Download
                        textColor={textColor}
                        button={benefitButton as SalesValues}
                        meta={meta}
                        benefitIndex={index + 1}
                        aria-label={`Download button for grid card ${index + 1}`}
                      />
                    ) : (
                      <Button
                        to={purchaseElementId}
                        button={benefitButton as SalesValues}
                        meta={meta}
                        benefitIndex={index + 1}
                        aria-label={`Action button for grid card ${index + 1}`}
                      />
                    )}
                  </BtnInner>
                </CardItem>
              );
            }
          )}
        </CardsInner>
      </Wrapper>
    </FlexBox>
  );
};

V15.displayName = 'PhotoV15';
